import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../constants/axiosInstance';
import { BatchConfigurationPayload } from '../types/BatchConfigType';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { BATCH_CONFIG_SAVED } from '../../../modules/analytics/events';

interface SaveBatchConfigurationResponse {
  id: string;
  message: string;
}

const saveBatchConfiguration = async (
  data: BatchConfigurationPayload
): Promise<SaveBatchConfigurationResponse> => {
  const response = await axiosInstance().post<SaveBatchConfigurationResponse>(
    '/trigger/createOrUpdateBatchComponent',
    data
  );
  return response.data;
};

export const useSaveBatchConfiguration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation<SaveBatchConfigurationResponse, Error, BatchConfigurationPayload>({
    mutationFn: saveBatchConfiguration,
    onSuccess: () => {
      trackEvent(BATCH_CONFIG_SAVED);
      toast.success(t('batchConfig.saveSuccess', 'Batch Configuration saved successfully'));
      queryClient.invalidateQueries({ queryKey: ['batchConfiguration'] });
      navigate('../');
    },
    onError: (error: Error) => {
      toast.error(error.message || t('batchConfig.saveError', 'Failed to save BatchConfiguration'));
    },
  });

  return {
    saveBatchConfigurationMutation: mutation.mutate,
    isSavingBatchConfiguration: mutation.isPending,
    saveBatchConfigurationError: mutation.error,
    savedBatchConfigurationData: mutation.data,
  };
};
